import { buildTextInputFieldData } from 'adapter/in/common/form/Text';
import { ConfigFieldType, FieldStatus, FormFieldData, FormFields, Serializable, WidgetValues } from 'adapter/in/common/form/types';
import { buildSelectorFieldData } from 'adapter/in/common/hooks/form/useBaseSelectorContainer';
import { FIELD_TYPE } from 'domain/entity/preset/field/fieldType';
import PresetFormField from 'domain/entity/preset/form/presetFormField';
import { ValueOptionConfigurationField, WidgetConfigurationField, WidgetOptionConfigurationField, WidgetType } from 'domain/entity/widget';
import WidgetData, { WidgetDataTypes } from 'domain/entity/widgetData';
import { FieldValues, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import ChartData from 'domain/entity/widgets/ChartData';
import GaugeData from 'domain/entity/widgets/GaugeData';
import TextData from 'domain/entity/widgets/TextData';
import SingleKPIData from 'domain/entity/widgets/SingleKPIData';
import { ABSOLUTE_PERCENTAGE_VALUES, ALIGN_TEXT, OPTIONS } from 'adapter/in/common/widget/options';

export const setFormValues = (setValue: UseFormSetValue<FieldValues>, fields: FormFields) =>
    Object.entries(fields).forEach(([key, value]) => {
        setValue(key, value);
    });

export const getFormState = (
    fields: PresetFormField[],
    configType: ConfigFieldType,
    configurationFields?: WidgetConfigurationField[] | WidgetOptionConfigurationField[],
    data?: WidgetDataTypes
): FormFields =>
    Object.assign(
        {},
        ...fields.map(({ key, type }: PresetFormField) => ({
            [key]: getValueBy(type, key, configType, configurationFields, data),
        }))
    );

const mapConfigurationFields = (configurationFields: WidgetConfigurationField[], fieldKey: string): string => {
    const foundConfigurationField = configurationFields.filter(
        (configurationField: WidgetConfigurationField) => configurationField.key === fieldKey
    )[0];

    return foundConfigurationField && typeof foundConfigurationField.value === 'string' ? foundConfigurationField.value : '';
};

const mapOptionConfigurationFields = (
    optionConfigurationFields: WidgetOptionConfigurationField[],
    fieldKey: string,
    data?: WidgetDataTypes
): string => {
    const foundOptionConfigurationFields = optionConfigurationFields.filter(
        (optionConfigurationField: WidgetOptionConfigurationField) => optionConfigurationField.key === fieldKey
    )[0];

    return foundOptionConfigurationFields && typeof foundOptionConfigurationFields.value === 'string'
        ? foundOptionConfigurationFields.value.toString()
        : mapDataToOptionConfigField(fieldKey, data);
};

const populateValueField = (optionFields: WidgetOptionConfigurationField[], key: string, initialValue: string) => {
    const value = optionFields.filter((configurationField) => configurationField.key === key)[0];
    return value ? value.value : initialValue;
};

export const isChartType = (widgetType: WidgetType): boolean =>
    widgetType === WidgetType.BAR || widgetType === WidgetType.STACKED_BAR || widgetType === WidgetType.LINE;
const isWidgetData = (data: WidgetDataTypes): data is WidgetData => 'type' in data && 'dataAvailability' in data && 'description' in data;
export const isGaugeData = (data: WidgetDataTypes): data is GaugeData =>
    (data as GaugeData).checkpoints !== undefined && (data as GaugeData).gaugeType !== undefined;
export const isChartData = (data: WidgetDataTypes): data is ChartData =>
    (data as ChartData).series !== undefined && (data as ChartData).axesX !== undefined && (data as ChartData).axesY !== undefined;
const isTextData = (data: WidgetDataTypes): data is TextData => 'header' in data && 'body' in data;
const isSingleKPIData = (data: WidgetDataTypes): data is SingleKPIData => 'type' in data && data.type === WidgetType.SINGLE_KPI;

export const mapDataToOptionConfigField = (fieldKey: string, data?: WidgetDataTypes): string => {
    if (!data) return '';
    switch (fieldKey) {
        case OPTIONS.CUSTOM_TITLE:
            return isWidgetData(data) ? data.description.title : '';
        case OPTIONS.MIN:
            return isGaugeData(data) ? String(data.min) : '';
        case OPTIONS.MAX:
            return isGaugeData(data) ? String(data.max) : '';
        case `${OPTIONS.ALIGN_TEXT}_header`:
        case `${OPTIONS.ALIGN_TEXT}_body`:
            return isTextData(data) ? ALIGN_TEXT.START : '';
        case OPTIONS.ABSOLUTE_PERCENTAGE:
            return isSingleKPIData(data) || isGaugeData(data) ? ABSOLUTE_PERCENTAGE_VALUES.PERCENTAGE : '';
        default:
            return '';
    }
};

const hasSameValueAndInitialValue = (initialValue: string, value: ValueOptionConfigurationField, key: string, data: WidgetDataTypes) =>
    initialValue === value && mapDataToOptionConfigField(key, data) === initialValue;

const getInitialStatus = (
    type: FIELD_TYPE,
    configType: ConfigFieldType,
    hasSameValue: boolean,
    fieldKey: string,
    optionFields?: WidgetOptionConfigurationField[]
) => {
    if (
        configType === ConfigFieldType.OPTIONS &&
        hasSameValue &&
        optionFields &&
        [FIELD_TYPE.COLOR_PICKER, FIELD_TYPE.TEXT].includes(type)
    ) {
        return optionFields.filter((optField) => optField.key === fieldKey)[0] ? FieldStatus.HAS_VALUE : FieldStatus.DEFAULT_DATA;
    }
    if (optionFields) return FieldStatus.INITIAL_VALUE;
    return FieldStatus.INIT;
};

export const getValueBy = (
    type: string,
    key: string,
    configType: ConfigFieldType,
    configurationFields?: WidgetConfigurationField[] | WidgetOptionConfigurationField[],
    data?: WidgetDataTypes
) => {
    let initialValue = '';
    if (configurationFields) {
        initialValue =
            configType === ConfigFieldType.DATA
                ? mapConfigurationFields(configurationFields as WidgetConfigurationField[], key)
                : mapOptionConfigurationFields(configurationFields, key, data);
    }
    const value =
        configurationFields && configType === ConfigFieldType.OPTIONS
            ? populateValueField(configurationFields, key, initialValue)
            : initialValue;
    const hasSameValue = data ? hasSameValueAndInitialValue(initialValue, value, key, data) : false;
    const status = getInitialStatus(type as FIELD_TYPE, configType, hasSameValue, key, configurationFields);

    switch (type) {
        case FIELD_TYPE.TEXT:
            return buildTextInputFieldData(status, initialValue, configType, value.toString());
        case FIELD_TYPE.SELECTOR:
        default:
            return buildSelectorFieldData(status, initialValue, configType);
    }
};

// Save form functions
export const asValues = ([key, value]: [string, Serializable]) => ({
    key,
    value: value.serialize(),
});

export const getValuesForm = (values: WidgetValues[]) =>
    values.map((value: WidgetValues) => new WidgetConfigurationField(value.key, value.value));

export const asValuesMap = (
    getValues: UseFormGetValues<FormFields>,
    map: (value: FormFieldData) => string,
    filter: (value: FormFieldData) => boolean = () => true
) =>
    Object.keys(getValues())
        .filter((key) => filter(getValues(key)))
        .map((key) => ({ key, value: map(getValues(key)) }));
